import { Link, Navigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { Overview } from '@arcnet/contracts';
import { api } from '../../lib/api/client.js';
import { useSession, useOwnOnly } from '../../lib/auth/session.js';
import { useCdrStats, useExtensions, useTenants } from '../../lib/api/hooks.js';
import { CardHead, SectionHead, Stat, StatGroup, StatusBadge, ErrorBanner, Empty } from '../../components/ui/Primitives.js';
import { SpendInsights } from '../manage/SpendInsights.js';
import { formatDuration, formatMoney, formatNumber } from '../../lib/format.js';
import { PlatformDashboard } from './PlatformDashboard.js';

export function DashboardPage() {
  // A self-service user's home is their own extensions, not the staff dashboard.
  const ownOnly = useOwnOnly();
  const session = useSession((s) => s.session);
  const activeTenantId = useSession((s) => s.activeTenantId);
  if (ownOnly) return <Navigate to="/my/extensions" replace />;
  /**
   * An account's own admin or manager lands in their account's TMS (owner, 26 September 2026: "they
   * should go straight into the call routing and features since its their own contained TMS"). By
   * id; the page puts the account's short name in the address.
   */
  const own = (session?.scope.level === 'tenant' || session?.scope.level === 'site') ? session.active_tenant_id : null;
  if (own) return <Navigate to="/manage/$tenantId" params={{ tenantId: own }} replace />;
  // Platform staff with every account in view get the charts; a reseller, or one account chosen, the summary.
  if (session?.scope.level === 'platform' && !activeTenantId) return <PlatformDashboard />;
  return <StaffDashboard />;
}

function StaffDashboard() {
  const session = useSession((s) => s.session);
  const activeTenantId = useSession((s) => s.activeTenantId);
  const isPlatform = session?.scope.level === 'platform';

  const tenants = useTenants({ limit: 5 });
  const one = useCdrStats(activeTenantId);
  const extensions = useExtensions(activeTenantId, { limit: 200 });
  /**
   * With no account chosen ("all accounts"), every account in reach at once. The per-account
   * queries above need an account and never ran, so this page used to show zeros and a card that
   * loaded for ever (26 September 2026).
   */
  const overview = useQuery({
    queryKey: ['overview'],
    queryFn: () => api.get<Overview>('/v1/overview'),
    enabled: !activeTenantId,
    refetchInterval: 60_000,
  });
  const all = !activeTenantId;
  const stats = all
    ? { data: overview.data?.totals, isLoading: overview.isLoading, error: overview.error }
    : one;

  const extCount = all ? overview.data?.extensions.total ?? 0 : extensions.data?.data.length ?? 0;
  const activeExt = all ? overview.data?.extensions.active ?? 0 : extensions.data?.data.filter((e) => e.status === 'active').length ?? 0;
  const extLoaded = all ? overview.data !== undefined : extensions.data !== undefined;

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Dashboard</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            {isPlatform ? 'Platform overview across all tenants' : 'Your phone system at a glance'}
          </p>
        </div>
      </div>

      {stats.error && <ErrorBanner error={stats.error} />}

      <div style={{ marginBottom: 18 }}>
        <StatGroup>
          <Stat
            bare icon="phone" hue={1}
            label="Calls (30 days)"
            value={stats.isLoading ? '—' : formatNumber(stats.data?.total_calls ?? 0)}
            sub={stats.data ? `${stats.data.answered_calls} answered` : undefined}
          />
          <Stat
            bare icon="check" hue={2}
            label="Answer rate"
            value={stats.isLoading ? '—' : `${stats.data?.answer_rate_pct ?? 0}%`}
            tone={
              stats.data && stats.data.total_calls > 0
                ? stats.data.answer_rate_pct >= 70 ? 'ok'
                : stats.data.answer_rate_pct >= 40 ? 'warn' : 'danger'
                : undefined
            }
            sub="of all calls"
          />
          <Stat
            bare icon="clock" hue={4}
            label="Talk time"
            value={stats.isLoading ? '—' : formatDuration(stats.data?.total_duration_sec ?? 0)}
            sub={stats.data ? `avg ${formatDuration(stats.data.avg_duration_sec)}` : undefined}
          />
          <Stat
            bare icon="billing" hue={3}
            label="Spend (30 days)"
            value={stats.isLoading ? '—' : formatMoney(stats.data?.spend ?? null, stats.data?.currency ?? 'ZAR')}
            sub="excluding VAT"
          />
        </StatGroup>
      </div>

      {/* Only with a tenant in context. At platform scope this would need to be
          a cross-tenant query, which is a different endpoint and a different
          page -- pretending otherwise would show one account's leaderboard
          under a heading that claims to cover all of them. */}
      {activeTenantId && (
        <>
          <SectionHead title="Where the spend went" />
          <div style={{ marginBottom: 18 }}>
            <SpendInsights tenantId={activeTenantId} />
          </div>
        </>
      )}

      <div className="arc-grid arc-grid-2">
        <div className="arc-card">
          <CardHead title="Extensions" sub={<span className="arc-num">{activeExt} of {extCount} active</span>} />
          {!extLoaded ? (
            <div className="arc-card-body">
              <div className="arc-skeleton" style={{ height: 60 }} />
            </div>
          ) : all ? (
            <div className="arc-table-wrap">
              <table className="arc-table">
                <thead><tr><th>Account</th><th className="arc-td-right">Extensions</th><th className="arc-td-right">Active</th></tr></thead>
                <tbody>
                  {overview.data!.accounts.filter((a) => a.extensions > 0).slice(0, 8).map((a) => (
                    <tr key={a.id}>
                      <td><Link to="/manage/$tenantId" params={{ tenantId: a.slug }} style={{ fontWeight: 550 }}>{a.name}</Link></td>
                      <td className="arc-td-right arc-num">{formatNumber(a.extensions)}</td>
                      <td className="arc-td-right arc-num">{formatNumber(a.active_extensions)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : extCount === 0 ? (
            <Empty icon="extension" title="No extensions yet" hint="Add an extension to start making calls." />
          ) : (
            <div className="arc-table-wrap">
              <table className="arc-table">
                <thead>
                  <tr><th>Number</th><th>Name</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {extensions.data?.data.slice(0, 6).map((e) => (
                    <tr key={e.id}>
                      <td className="arc-mono">{e.number}</td>
                      <td>{e.display_name}</td>
                      <td><StatusBadge status={e.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="arc-card">
          <CardHead title={isPlatform ? 'Tenants' : 'Call mix (30 days)'} />
          {isPlatform ? (
            <div className="arc-table-wrap">
              <table className="arc-table">
                <thead>
                  <tr><th>Name</th><th className="arc-td-right">Calls (30 days)</th><th className="arc-td-right">Spend</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {(overview.data?.accounts ?? []).slice(0, 8).map((t) => (
                    <tr key={t.id}>
                      <td><Link to="/manage/$tenantId" params={{ tenantId: t.slug }} style={{ fontWeight: 550 }}>{t.name}</Link></td>
                      <td className="arc-td-right arc-num">{formatNumber(t.calls)}</td>
                      <td className="arc-td-right arc-num">{formatMoney(t.spend, t.currency ?? 'ZAR')}</td>
                      <td><StatusBadge status={t.status} /></td>
                    </tr>
                  ))}
                  {overview.data === undefined && tenants.data?.data.map((t) => (
                    <tr key={t.id}>
                      <td style={{ fontWeight: 550 }}>{t.name}</td>
                      <td /><td />
                      <td><StatusBadge status={t.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="arc-card-body">
              <dl className="arc-kv">
                <dt>Inbound</dt><dd className="arc-num">{stats.data?.inbound ?? 0}</dd>
                <dt>Outbound</dt><dd className="arc-num">{stats.data?.outbound ?? 0}</dd>
                <dt>Internal</dt><dd className="arc-num">{stats.data?.internal ?? 0}</dd>
              </dl>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
